import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import Select from "react-select";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { createTeacherPassage } from "../../../../api/crmContent.service";
import { getAllgrade } from "../../../../api/grade.service";
import { ISelectElement } from "../../../../model/interface/keyValuePair";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import store from "../../../../redux/store";

interface INewPassageModalProps {
  userId: number;
  onClose: () => void;
}

const defaultGradeOption: ISelectElement = { label: "Select", value: "0" };

export const NewPassageModal: React.FC<INewPassageModalProps> = ({
  userId,
  onClose,
}) => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cmsAuthorUserId: number =
    (store.getState() as any).profile?.userId ?? 0;

  const [grades, setGrades] = useState<Array<ISelectElement>>([]);
  const [selectedGrade, setSelectedGrade] =
    useState<ISelectElement>(defaultGradeOption);
  const [title, setTitle] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);

  useEffect(() => {
    getAllgrade().then((response) => {
      const options: Array<ISelectElement> = (response.data?.data ?? []).map(
        (grade: { gradeId: number; name: string }) => ({
          value: String(grade.gradeId ?? 0),
          label: grade.name,
        })
      );
      setGrades([defaultGradeOption, ...options]);
    });
  }, []);

  const canCreate = Number(selectedGrade.value) > 0 && title.trim().length > 0;

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    createTeacherPassage({
      userId,
      gradeId: Number(selectedGrade.value),
      title: title.trim(),
      isApproved: false,
      isActive: true,
      cmsAuthorUserId: cmsAuthorUserId || undefined,
    })
      .then((response) => {
        const newId = response.data;
        navigate(`/crm/users/${userId}/content/passages/${newId}`);
      })
      .catch(() => toast.error("Failed to create passage."))
      .finally(() => setCreating(false));
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex border-b pb-3">
                <DialogTitle
                  as="h2"
                  className="text-xl font-semibold text-gray-700"
                >
                  New Passage
                </DialogTitle>
              </div>

              <form onSubmit={handleCreate}>
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Grade
                    </label>
                    <Select
                      options={grades}
                      value={selectedGrade}
                      onChange={(option) =>
                        setSelectedGrade(option ?? defaultGradeOption)
                      }
                      className="w-48"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="new-passage-title"
                      className="text-sm font-medium text-gray-700"
                    >
                      Title
                    </label>
                    <input
                      id="new-passage-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter passage title"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm"
                    />
                  </div>
                </div>

                <div className="flex pt-4">
                  <button
                    type="submit"
                    disabled={!canCreate || creating}
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
